import { scaleLinear, scaleTime } from "@visx/scale";
import { extent, max, min } from "d3-array";
import type { YFormatType } from "../context/ChartContext";
import { formatComponentValue } from "./formatting";

export { resolveAxisFormatter } from "./chartAxisFormat";

export type { YFormatType };

/**
 * A banded area series' lower bound: a constant, or resolved per point.
 *
 * Structurally identical to `AreaSeriesBaseline`, restated here so this module
 * does not have to import from a component.
 */
export type TooltipBaseline = number | ((point: BaseDataPoint, index: number) => number);

/**
 * Resolve a banded area series' lower bound at a point, for the min-max range
 * tooltip. Returns undefined unless the series opted in via `tooltipRange` and
 * the bound is a finite number distinct from `yValue`.
 *
 * Shared rather than owned by `ChartContainer`, because the container's hover
 * path is not the only one that builds tooltip rows: an event span covers the
 * plot and reports the series under the cursor itself. When only the container
 * knew about baselines, a band representing 241-246 read as the single value 246
 * for as long as the pointer was over a span.
 */
export const resolveBaselineValue = (
  meta: { baseline?: TooltipBaseline; tooltipRange?: boolean },
  point: BaseDataPoint,
  index: number
): number | undefined => {
  if (!meta.tooltipRange || meta.baseline === undefined) return undefined;
  const bound = typeof meta.baseline === "function" ? meta.baseline(point, index) : meta.baseline;
  return typeof bound === "number" && Number.isFinite(bound) && bound !== point.yValue ? bound : undefined;
};

export interface BaseDataPoint {
  xValue: Date;
  /**
   * The data value for this point. This is the value reported to tooltips and
   * CSV exports, and — by default — also the y-axis coordinate where the point
   * is drawn.
   *
   * For stacked or banded charts where the *drawn* position differs from the
   * data value (e.g. each band is drawn at its cumulative stack top), set
   * `chartYValue` on the point instead of mutating `yValue`. That keeps tooltip
   * and export output honest while letting the chart draw the visual stack.
   */
  yValue: number;
  /**
   * Optional render-only override for where to draw this point on the y-axis.
   * When set, series components (e.g. `AreaSeries`, `LineSeries`) draw the
   * point at `chartYValue` instead of `yValue`. Tooltip values and CSV exports
   * continue to use `yValue`, so the user-facing number reflects the underlying
   * data and not the stacked coordinate.
   *
   * Used for stacked area charts where each band's drawn y is the cumulative
   * top of the stack, but the displayed value should be the band's own
   * magnitude. Existing non-stacked consumers can leave this undefined and
   * behavior is unchanged.
   */
  chartYValue?: number;
  /**
   * Optional lower bound for a banded `AreaSeries` (e.g. a min–max envelope or a
   * stacked band's floor). Carried on the point so the floor stays bound to the
   * same sample as the ceiling and survives `ChartContainer`'s zoom-filtering —
   * a positional baseline array would desync once `data` is re-indexed. Read via
   * `baselineFromPoint`.
   */
  baselineValue?: number;
  /**
   * Optional render-only control for the tooltip focus marker. Defaults to true.
   * Useful for zero-height stacked bands: the tooltip row should still report
   * the zero value, but drawing a marker at the current stack baseline makes the
   * band look active when it is not.
   */
  showTooltipMarker?: boolean;
  category?: string;
  /**
   * Optional extra rows to display in the tooltip for this data point, below
   * the series value and category rows. Purely presentational — does not
   * affect chart drawing, scales, or CSV export. Used by consumers that need
   * to surface point-level context (e.g. per-sample geolocation status like
   * home/away). Each row is `{ label, value }` and rendered as
   * `<label>: <value>` in the tooltip.
   */
  extraTooltipRows?: Array<{ label: string; value: string }>;
}

/**
 * Canonical `AreaSeries` baseline accessor: the per-point band floor. Pass as
 * `baseline={baselineFromPoint}` for any banded area whose points carry a
 * `baselineValue`. Because the floor is read off the point, it stays correct
 * under zoom/pan where a positional baseline array would desync.
 */
export const baselineFromPoint = (point: BaseDataPoint): number => point.baselineValue ?? 0;

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const defaultMargin: ChartMargin = {
  // Air for tick labels anchored at the plot's top edge.
  top: 8,
  right: 0,
  bottom: 50,
  left: 60,
};

export interface YFormatSettings {
  format: (value: number) => string;
  min?: number;
  max?: number;
  tickInterval?: number;
  tickFormat?: (value: number) => string;
}

/**
 * Get Y-axis format settings from a ComponentFormatter
 * Uses the main formatting system for consistency across all components
 */
export function getYFormatSettings(formatter: YFormatType = { type: "number" }): YFormatSettings {
  const safeFormat = (value: number | null | undefined): string => {
    if (value == null) return "-";
    const result = formatComponentValue({ value, formatter });
    // formatComponentValue returns ReactNode, convert to string
    if (result === null) return "-";
    if (typeof result === "string") return result;
    if (typeof result === "number") return result.toString();
    // For React elements, try to extract text content if possible
    // This handles the case where formatComponentValue returns JSX with units
    if (typeof result === "object" && result !== null && "props" in result) {
      // Extract text from React element children
      const extractText = (node: unknown): string => {
        if (node == null) return "";
        if (typeof node === "string") return node;
        if (typeof node === "number") return node.toString();
        if (typeof node === "object" && "props" in node) {
          const props = (node as { props?: { children?: unknown } }).props;
          if (props?.children) {
            if (Array.isArray(props.children)) {
              return props.children.map(extractText).join("");
            }
            return extractText(props.children);
          }
        }
        return "";
      };
      const text = extractText(result);
      return text || value.toString();
    }
    // Fallback to raw value for chart axes
    return value.toString();
  };

  // Create a simplified tick format for axis labels (less verbose)
  const tickFormat = (value: number | null | undefined): string => {
    if (value == null) return "-";

    // Handle string shorthand formatters
    if (typeof formatter === "string") {
      const result = formatComponentValue({ value, formatter });
      if (result === null) return "-";
      return String(result);
    }

    // For specific format types, provide concise axis labels
    if (typeof formatter === "object" && formatter.type) {
      switch (formatter.type) {
        case "energy":
        case "power":
        case "voltage":
        case "current":
        case "resistance":
        case "temperature":
        case "distance":
          // These formatters include units, which is good for axes
          return safeFormat(value);

        case "currency":
          // For currency, just use the standard format
          return safeFormat(value);

        default:
          return safeFormat(value);
      }
    }

    return safeFormat(value);
  };

  // Determine min/max/intervals based on format type
  let min: number | undefined;
  let max: number | undefined;
  let tickInterval: number | undefined;

  if (typeof formatter === "object" && formatter.type) {
    switch (formatter.type) {
      case "number":
        if ("min" in formatter) min = formatter.min as number | undefined;
        if ("max" in formatter) max = formatter.max as number | undefined;
        break;
    }
  }

  return {
    format: safeFormat,
    tickFormat,
    min,
    max,
    tickInterval,
  };
}

export const createXScale = (data: BaseDataPoint[], width: number) => {
  const dateExtent = extent(data, (d) => d.xValue) as [Date, Date];
  return scaleTime({
    range: [defaultMargin.left, width - defaultMargin.right],
    domain: dateExtent,
    nice: true,
  });
};

export const createYScale = (
  data: BaseDataPoint[],
  height: number,
  formatType: YFormatType,
  explicitDomain?: [number, number]
) => {
  const yFormatSettings = getYFormatSettings(formatType);
  if (explicitDomain) {
    return scaleLinear({
      range: [height - defaultMargin.bottom, defaultMargin.top],
      domain: explicitDomain,
      nice: true,
    });
  }
  // Domain considers the data value, the render-only override (`chartYValue`)
  // so stacked bands fit inside the y-axis even when their drawn coordinate is
  // far from their reported value, and the band floor (`baselineValue`) so
  // min–max range bands aren't clipped at the plot edge.
  const dataYMin = min(data, (d) => Math.min(d.yValue, d.chartYValue ?? d.yValue, d.baselineValue ?? d.yValue)) || 0;
  const dataYMax = max(data, (d) => Math.max(d.yValue, d.chartYValue ?? d.yValue, d.baselineValue ?? d.yValue)) || 0;
  const yMin =
    yFormatSettings.min !== undefined
      ? yFormatSettings.min
      : dataYMin >= 0
        ? 0 // 0 if all positive values, min is 0
        : dataYMin * 1.1; // else scale the negative values by 10% to make room for the y-axis values
  const yMax =
    yFormatSettings.max !== undefined
      ? yFormatSettings.max
      : dataYMax <= 0
        ? 0 // If all values are negative, max is 0
        : dataYMax * 1.1; // else scale the positive values by 10% to make room for the y-axis values
  return scaleLinear({
    range: [height - defaultMargin.bottom, defaultMargin.top],
    domain: [yMin, yMax],
    nice: true,
  });
};
